import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Toast, type ToastProps } from './Toast';
import type { FeedbackType } from '../shared/feedbackHelpers';
import './Toast.css';

export type ToastEntry = {
  id: number;
  feedbackType: FeedbackType;
  title: ReactNode;
  content?: ReactNode;
  detailed?: boolean;
  duration: number;
};

/** Container that renders queued toasts via a portal. Mount once near the app root. */
export type ToastHostProps = {
  className?: string;
  children?: ReactNode;
};

let toastIdCounter = 0;
let addToastFn: AddToastFn | null = null;

type AddToastFn = (
  feedbackType: FeedbackType,
  title: ReactNode,
  options?: { content?: ReactNode; detailed?: boolean; duration?: number },
) => number;

/**
 * Toast host portal — renders queued toasts.
 * Mount once near your app root (e.g. `<ToastHost />`).
 * Then use `sparkToast.success("Done!")` from anywhere.
 */
export function ToastHost({ className = '' }: ToastHostProps) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const removeToast = useCallback((id: number) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast: AddToastFn = useCallback(
    (feedbackType, title, options) => {
      const id = ++toastIdCounter;
      const duration = options?.duration ?? 3000;
      setToasts((prev) => [
        ...prev,
        {
          id,
          feedbackType,
          title,
          content: options?.content,
          detailed: options?.detailed,
          duration,
        },
      ]);
      if (duration > 0) {
        timersRef.current[id] = setTimeout(() => removeToast(id), duration);
      }
      return id;
    },
    [removeToast],
  );

  useEffect(() => {
    addToastFn = addToast;
    return () => {
      addToastFn = null;
    };
  }, [addToast]);

  useEffect(
    () => () => {
      Object.values(timersRef.current).forEach(clearTimeout);
      timersRef.current = {};
    },
    [],
  );

  if (toasts.length === 0) return null;

  const rootClasses = ['spark-toast-portal', className]
    .filter(Boolean)
    .join(' ');

  return createPortal(
    <div className={rootClasses} aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className="spark-toast-portal-item">
          <Toast
            title={t.title}
            content={t.content}
            feedbackType={t.feedbackType}
            detailed={t.detailed}
            /* Simple toasts: icon + title only (no Close). Detailed toasts keep
               the corner X dismiss; footer actions stay off unless a caller
               renders <Toast> directly. */
            showButtons={false}
            showIconButton
            onCloseClick={() => removeToast(t.id)}
            onDismissClick={() => removeToast(t.id)}
          />
        </div>
      ))}
    </div>,
    document.body,
  );
}

/**
 * Imperative toast API — call from anywhere after `<ToastHost />` is mounted.
 *
 * ```ts
 * sparkToast.success('Saved!');
 * sparkToast.error('Upload failed', { duration: 5000 });
 * ```
 */
export const sparkToast = {
  success: (title: ReactNode, options?: { content?: ReactNode; detailed?: boolean; duration?: number }) =>
    addToastFn?.('success', title, options) ?? -1,
  error: (title: ReactNode, options?: { content?: ReactNode; detailed?: boolean; duration?: number }) =>
    addToastFn?.('error', title, options) ?? -1,
  info: (title: ReactNode, options?: { content?: ReactNode; detailed?: boolean; duration?: number }) =>
    addToastFn?.('info', title, options) ?? -1,
  warning: (title: ReactNode, options?: { content?: ReactNode; detailed?: boolean; duration?: number }) =>
    addToastFn?.('warning', title, options) ?? -1,
};
